import {AGENCY_KEYS} from "@/api/agencyApi";
import RightChevronIcon from "@/assets/colorable/right_chevron.svg?react";
import Refluenced from "@/assets/images/refluenced.svg?react";
import Button from "@/components/Common/Button";
import AgencyRegister_01 from "@/components/MultiAccount/CreateAgency_01";
import AgencyRegister_02 from "@/components/MultiAccount/CreateAgency_02";
import AgencyRegister_03 from "@/components/MultiAccount/CreateAgency_03";
import AgencyRegister_04 from "@/components/MultiAccount/CreateAgency_04";
import AgencyRegister_05 from "@/components/MultiAccount/CreateAgency_05";
import {client} from "@/httpClient";
import {queryClient} from "@/queryClient";
import {useCreateAgencyStore, type TCreateAgencyFormState} from "@/store/createAgency";
import useCustomerFilterStore from "@/store/customerFilter";
import useUserStore from "@/store/user";
import type {TError} from "@/utils/errorHandlers";
import {useMutation} from "@tanstack/react-query";
import {createFileRoute, useRouter} from "@tanstack/react-router";
import {isEmpty} from "lodash";
import {useState} from "react";
import {useTranslation} from "react-i18next";
import {useTitle} from "react-use";

export const Route = createFileRoute("/_authenticated/_admin/admins/agency/create")({
  component: CreateAgency,
});

function CreateAgency() {
  const {t} = useTranslation(["multi_account", "common", "navigation"]);

  useTitle(t("navigation:create_new_multi_account_user"));

  const navigate = Route.useNavigate();
  const router = useRouter();
  const stepWeights = [0.35, 0.5, 0.85, 1.1, 1.0];
  const campaigns = useCreateAgencyStore(state => state.form.number_of_campaigns);
  const customerListSearch = useCustomerFilterStore(state => state.filters.search);
  const errors = useCreateAgencyStore(state => state.errors);
  const largeBrand = useCreateAgencyStore(state => state.form.large_brand);
  const reset = useCreateAgencyStore(state => state.reset);
  const sales_email = useUserStore(state => state.user.email);
  const seats = useCreateAgencyStore(state => state.form.number_of_seats);
  const setter = useCreateAgencyStore(state => state.setter);
  const headerKeys = [
    "what_kind_of_company_will_use_refluenced",
    largeBrand ? "large_brand_details" : "agency_details",
    "what_are_the_benefits_of_the_deal",
    "plan_details",
    "get_an_email_from_the_main_user_of_the_account",
  ] as const;
  const [step, setStep] = useState(1);

  const {mutateAsync} = useMutation({
    meta: {skipErrorToast: true, skipSuccessToast: true},
    mutationKey: AGENCY_KEYS.mAgencyPreRegister,
    mutationFn: ({data}: {data: TCreateAgencyFormState}) => client.post(AGENCY_KEYS.mAgencyPreRegister[0], data),
    onSuccess: () => {
      setter(state => {
        state.errors = null;
      });
      queryClient.invalidateQueries({queryKey: AGENCY_KEYS.qAgencyList(customerListSearch)});
      navigate({to: "/admins/customers"});
    },
    onError: (error: TError) => {
      setter(state => {
        state.errors = error.response?.data!;
      });
    },
  });

  return (
    <div className="bg-primary-midnight-dark flex min-h-screen flex-col items-center gap-6 py-8">
      <div className="max-w-wide container flex flex-col gap-10 px-8">
        <div>
          <button onClick={() => router.history.back()} className="mb-8 flex items-center text-white">
            <RightChevronIcon className="mr-2 rotate-180" />
            <span className="text-lg">{t("cancel")}</span>
          </button>
          <div className="flex flex-col text-white md:flex-row md:items-center md:justify-between">
            <div className="flex items-center gap-5">
              <Refluenced className="h-24 w-auto shrink-0 rounded-full bg-white p-4" />
              <h1 className="text-2.5xl mt-4 font-semibold">{t(headerKeys[step - 1])}</h1>
            </div>
            <div className="ml-3 flex w-1/3 flex-col items-end self-end md:w-1/5">
              <p className="text-xl font-medium">{step}/5</p>
              <div className="flex h-4 w-full items-center rounded-full bg-white p-0.5">
                <div
                  className="h-3 self-center rounded-full bg-linear-(--primary-dark-gradient) transition-[width] duration-300 ease-in-out"
                  style={{
                    width: `${((step * stepWeights[step - 1]) / 5) * 100}%`,
                  }}
                />
              </div>
            </div>
          </div>
        </div>
        <div className="border-neutral-grey-light flex flex-col gap-8 rounded-xl border bg-white p-5 text-black">
          {step === 1 && <AgencyRegister_01 />}
          {step === 2 && <AgencyRegister_02 />}
          {step === 3 && <AgencyRegister_03 />}
          {step === 4 && <AgencyRegister_04 />}
          {step === 5 && <AgencyRegister_05 />}
          <div className="flex w-full flex-row justify-between">
            {step > 1 && (
              <Button buttonStyle="White" onClick={() => setStep(prev => prev - 1)} className="w-2/5 self-start">
                {t("back")}
              </Button>
            )}
            {step < 5 && (
              <Button
                dataTestId="create-agency-continue-button"
                buttonStyle="Blue"
                onClick={() => setStep(prev => prev + 1)}
                isDisabled={largeBrand === null}
                className={`w-2/5 ${step > 1 ? "self-end" : "ml-auto"}`}>
                {t("continue")}
              </Button>
            )}
            {step === 5 && (
              <Button
                dataTestId="create-agency-activate-button"
                buttonStyle="Gradient"
                onClick={async () =>
                  await mutateAsync(
                    {
                      data: {
                        ...useCreateAgencyStore.getState().form,
                        cash_reward_service_fee_percentage:
                          useCreateAgencyStore.getState().form.influencer_fees.regular,
                        number_of_campaigns: campaigns || 100_000,
                        number_of_seats: seats || 100_000,
                        influencers_per_campaign:
                          useCreateAgencyStore.getState().form.influencers_per_campaign || 100_000,
                        invitations_per_campaign:
                          useCreateAgencyStore.getState().form.invitations_per_campaign || 100_000,
                        pro_service_fee_percentage: useCreateAgencyStore.getState().form.influencer_fees.pro,
                        sales_email: sales_email!,
                      },
                    },
                    {
                      onError: e => console.error(e),
                      onSuccess: () => {
                        reset();
                        setStep(1);
                      },
                    },
                  )
                }
                className={`w-2/5 ${step > 1 ? "self-end" : "ml-auto"}`}>
                {t("activate_account")}
              </Button>
            )}
          </div>
        </div>
      </div>
      {!!errors && !!Object.values(errors).find(err => !isEmpty(err)) && (
        <div className="bg-pretty-pink-200 text-danger-dark-red max-w-wide container rounded-sm px-1.5 py-3 font-semibold">
          <ul className="mb-0 pl-5">
            {Object.keys(errors).map(errorKey => {
              const fieldErrors = errors[errorKey];

              if (isEmpty(fieldErrors)) {
                return null;
              }

              return (
                <li key={`errors-${errorKey}`}>
                  {t(errorKey as "agency_name")}:
                  <ul className="pl-5">
                    {fieldErrors instanceof Array ? (
                      fieldErrors.map((error, index) => (
                        <li key={`${errorKey}-error-${index}`}>{t(error as "agency_name_required")}</li>
                      ))
                    ) : (
                      <li key={`${errorKey}-error`}>{t(fieldErrors as "agency_name_required")}</li>
                    )}
                  </ul>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
}
